import { FC, useMemo } from "react"
import { useTranslation } from "react-i18next"

import Text from "components/Text/Text"
import { IApiLink } from "@/types"
import useAssetVersions from "../hooks/useAssetVersions"
import VersionCollapsible from "../VersionCollapsible"
import AssetDocuments from "components/AssetDocuments/AssetDocuments"
import toastMessages from "stores/toastMessages"

interface IAssetDocumentsWithVersionsProps {
    lmid?: string
    linkGetAssetDocuments?: IApiLink
    linkAddAssetDocuments?: IApiLink
    linkGetAssetVersions?: IApiLink
    currentAssetVersion?: number
    isCurrentVersion?: boolean
}

const AssetDocumentsWithVersions: FC<IAssetDocumentsWithVersionsProps> = ({
    lmid,
    linkGetAssetDocuments,
    linkAddAssetDocuments,
    linkGetAssetVersions,
    currentAssetVersion,
    isCurrentVersion = true,
}) => {
    const { t } = useTranslation()
    const { assetVersions } = useAssetVersions(linkGetAssetVersions, toastMessages.showSystemError)

    // Sort versions: current first, then by version number descending
    const sortedVersions = useMemo(() => {
        if (!assetVersions) return null
        return [...assetVersions].sort((a, b) => {
            if (a.isCurrentVersion) return -1
            if (b.isCurrentVersion) return 1
            return b.version - a.version
        })
    }, [assetVersions])

    // Find the version being viewed
    const viewedVersion = useMemo(() => {
        if (!assetVersions || currentAssetVersion === undefined) return null
        return assetVersions.find(v => v.version === currentAssetVersion)
    }, [assetVersions, currentAssetVersion])

    // If viewing a previous version, only show that version's content (no version dropdowns)
    if (!isCurrentVersion) {
        return (
            <AssetDocuments
                lmid={lmid}
                linkGetAssetDocuments={viewedVersion?.linkGetAssetDocuments ?? linkGetAssetDocuments}
                linkAddAssetDocuments={undefined} // Don't allow adding to old versions
            />
        )
    }

    // If no versions available or just one version, show the regular AssetDocuments
    if (!linkGetAssetVersions || !sortedVersions || sortedVersions.length <= 1) {
        return (
            <AssetDocuments
                lmid={lmid}
                linkGetAssetDocuments={linkGetAssetDocuments}
                linkAddAssetDocuments={linkAddAssetDocuments}
            />
        )
    }

    // Get previous versions (non-current)
    const previousVersions = sortedVersions.filter(v => !v.isCurrentVersion)

    return (
        <>
            {/* Current version content shown directly without dropdown */}
            <AssetDocuments
                lmid={lmid}
                linkGetAssetDocuments={linkGetAssetDocuments}
                linkAddAssetDocuments={linkAddAssetDocuments}
            />

            {/* Previous versions shown as collapsible dropdowns */}
            {previousVersions.length > 0 && (
                <div className="mt-4">
                    <Text type="h4" className="mb-2 color-grey-600">{t("asset.previousVersions")}</Text>
                    {previousVersions.map((version) => (
                        <VersionCollapsible
                            key={version.assetId}
                            version={version}
                            defaultExpanded={false}
                        >
                            {(isExpanded) => isExpanded && (
                                version.linkGetAssetDocuments ? (
                                    <AssetDocuments
                                        lmid={lmid}
                                        linkGetAssetDocuments={version.linkGetAssetDocuments}
                                        linkAddAssetDocuments={undefined}
                                        hideTitle
                                    />
                                ) : (
                                    <Text type="p" className="color-grey-500 py-4">
                                        {t("documents.noDocumentsUploadedYet")}
                                    </Text>
                                )
                            )}
                        </VersionCollapsible>
                    ))}
                </div>
            )}
        </>
    )
}

export default AssetDocumentsWithVersions
